
import React from 'react';
import { KnowledgeEntry } from "./types";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EntryStatusBadge } from "./card-sections/EntryStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Trash2 } from "lucide-react";
import { format } from 'date-fns';

interface KnowledgeEntryCardProps {
  entry: KnowledgeEntry;
  onView?: (entry: KnowledgeEntry) => void;
  onDelete?: (entry: KnowledgeEntry) => void;
}

export function KnowledgeEntryCard({ entry, onView, onDelete }: KnowledgeEntryCardProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Extract summary from metadata if available
  const summary = entry.metadata?.summary as string || null;
  const fileType = entry.file_type || (entry.metadata?.content_type as string) || null;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start gap-2">
          <CardTitle className="text-lg font-semibold truncate" title={entry.title}>
            {entry.title}
          </CardTitle>
          
          <EntryStatusBadge status={entry.embedding_status} />
        </div>
        <CardDescription className="text-sm mt-1">
          {entry.created_at && (
            <span>Created {formatDate(entry.created_at)}</span>
          )}
          {fileType && <span className="ml-2 text-xs opacity-70">{fileType}</span>}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pb-2 flex-grow">
        {/* Display summary if available, otherwise show content preview */}
        {summary ? (
          <div className="text-sm text-muted-foreground line-clamp-4 mb-2">
            <span className="font-medium text-xs text-primary">Summary: </span>
            {summary}
          </div>
        ) : (
          entry.content && (
            <div className="text-sm text-muted-foreground line-clamp-4 mb-2">
              {entry.content}
            </div>
          )
        )}
        
        {entry.tags && entry.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {entry.tags.slice(0, 5).map((tag, index) => (
              <Badge key={index} variant="outline" className="px-1.5 py-0.5 text-xs">
                {tag}
              </Badge>
            ))}
            {entry.tags.length > 5 && (
              <Badge variant="outline" className="px-1.5 py-0.5 text-xs">
                +{entry.tags.length - 5} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="pt-2 flex justify-end gap-2 mt-auto">
        {onDelete && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(entry)}
            aria-label="Delete entry"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        
        {onView && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(entry)}
            aria-label="View entry"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
